// To make the widget height fully responsive, we've added a CSS class for aspect ratio (2:1), 
// width 100%, height auto, min-height 180px, and applied it to the chart container div. 
// We've also set Highcharts chart.height to '100%' and ensured all parent containers use h-full/flex-1.
// This approach allows the chart to adapt to different screen sizes while maintaining a consistent aspect ratio.

import React, { useRef, useEffect, useMemo } from "react";
import { CardContent } from "@/components/ui/card";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts/highcharts";
import "./widgets.css";
import { useDashboardData } from "@/services/dashboardDataService";
import { useWidgetStatusReporter } from "@/contexts/DashboardWidgetContext";
import { Skeleton } from "@/components/ui/skeleton";
import { cleanDateAndTimeObject } from "@shared/renderDate";

// Define the types for TypeScript
type ScorePoint = {
  date: Date;
  score: number;
};

type MonthlyScoreData = {
  y: number;
  name: string;
  allScores?: ScorePoint[];
};

type SeriesType = {
  name: string;
  data: Array<number | MonthlyScoreData | [number, number]>;
  marker: { symbol: string };
  color?: string;
};

const SupplyChainRisksChartWidget: React.FC = () => {
  // Use a ref to store the chart instance
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  // Fetch data from the dashboard data service
  const { data: collectorData, isLoading, error } = useDashboardData('supply-chain-risks');

  // Report widget status to dashboard context
  useWidgetStatusReporter('supply-chain-risks', isLoading, error, !!collectorData?.series);

  // Process the data for the chart
  const chartSeries = useMemo<SeriesType[]>(() => {
    if (!collectorData?.series) return [];
    
    let data = (collectorData.series || []).map((series: any, index: number) => {
      // Collect all data points, including allScores
      let allPoints: [number, number][] = [];
      
      // Transform data points
      series.data.forEach((point: any) => {
        if (typeof point === 'number' || !point) return;
        
        // If we have the enhanced data structure with allScores
        if (point.allScores && Array.isArray(point.allScores)) {
          // Add all scores from the allScores array
          point.allScores.forEach((scorePoint: any) => {
            if (scorePoint.date && scorePoint.score) {
              const timestamp = typeof scorePoint.date === 'string' ? 
                cleanDateAndTimeObject(scorePoint.date).getTime() : 
                cleanDateAndTimeObject(scorePoint.date).getTime();
              
              allPoints.push([timestamp, scorePoint.score]);
            }
          });
        } else {
          // Handle the previous data format (single point)
          const date = new Date(point.name);
          const timestamp = cleanDateAndTimeObject(date).getTime();
          allPoints.push([timestamp, point.y]);
        }
      });
      
      // Sort by timestamp (date)
      allPoints.sort((a, b) => a[0] - b[0]);
      
      // Remove duplicates with exactly the same timestamp and score (optional)
      allPoints = allPoints.filter((point, index, arr) => 
        index === 0 || point[0] !== arr[index - 1][0] || point[1] !== arr[index - 1][1]
      );

      console.log(`[SupplyChainRisksChartWidget] ${series.name} - Found ${allPoints.length} data points`);

      return {
        name: series.name,
        data: allPoints,
        marker: { symbol: series.marker?.symbol || 'circle' },
        // Use a color from the Highcharts default colors
        color: Highcharts.getOptions().colors?.[index % (Highcharts.getOptions().colors?.length || 1)]
      };
    });

    console.log('[SupplyChainRisksChartWidget] chartSeries:', data);

    return data;
  }, [collectorData]);

  // Chart options
  const chartOptions: Highcharts.Options = useMemo(() => ({
    chart: {
      type: 'scatter',
      zooming: {
        type: 'xy',
      },
      backgroundColor: 'transparent',
      // Do not set width or height here! Control size via container CSS only.
    },
    title: {
      text: null, // Hide title
    },
    menu: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    yAxis: {
      title: {
        text: 'Upguard Score',
      },
      labels: {
        format: '{value}',
      },
      tickInterval: 100,
      min: 0,
      max: 1000,
      gridLineWidth: 1,
      gridLineColor: 'rgba(0, 0, 0, 0.05)',
    },
    xAxis: {
      type: 'datetime',
      title: {
        text: 'Date',
      },
      labels: {
        format: '{value:%b %Y}', // Format the date
      },
      startOnTick: true,
      endOnTick: true,
      showLastLabel: true,
      gridLineWidth: 1,
      gridLineColor: 'rgba(0, 0, 0, 0.05)',
    },
    legend: {
      enabled: false,
    },
    tooltip: {
      formatter: function() {
        const date = new Date(this.x);
        const formattedDate = date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
        });
        return `<b>${this.series.name}</b><br/>` +
               `Date: ${formattedDate}<br/>` +
               `Score: ${this.y}/1000`;
      },
      useHTML: true,
      borderWidth: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      shadow: true,
      style: {
        padding: '10px',
      },
    },
    plotOptions: {
      scatter: {
        marker: {
          radius: 2.5,
          symbol: 'circle',
          states: {
            hover: {
              enabled: true,
              lineColor: 'rgb(100,100,100)',
              lineWidth: 1,
              radiusPlus: 2,
              lineWidthPlus: 1
            },
          },
        },
        states: {
          hover: {
            enabled: true,
            lineWidthPlus: 0
          },
        },
      },
    },
    series: chartSeries as any[],
  }), [chartSeries]);

  // Handle window resize for responsiveness
  useEffect(() => {
    const handleResize = () => {
      if (chartRef.current) {
        chartRef.current.chart.reflow();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <CardContent className="h-full w-full flex flex-col items-center justify-center" style={{ containerType: 'inline-size', padding: 'min(2cqw, 1rem)' }}>
      <h3 className="font-semibold mb-2 text-center">Supply-Chain Risks</h3>
      {isLoading ? (
        <Skeleton className="w-full h-full" />
      ) : error ? (
        <div className="flex flex-col items-center justify-center w-full h-full py-8">
          <span className="text-3xl mb-2">🚨</span>
          <span className="scalable-text-small text-muted-foreground">Failed to load data</span>
        </div>
      ) : (
        <div
          style={{
            aspectRatio: '2 / 1',
            width: '100%',
            height: '100%',
            minHeight: 180,
            minWidth: 260,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          className="w-full h-full"
        >
          <HighchartsReact
            ref={chartRef}
            highcharts={Highcharts}
            options={chartOptions}
            containerProps={{ style: { width: '100%', height: '100%' } }}
          />
        </div>
      )}
    </CardContent>
  );
};

export default SupplyChainRisksChartWidget;
